import React, { useEffect, useRef, useState } from "react";
import logo from "../../assets/logo/logo.png";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { otpVerify, requestOtp, resetPassword } from "../../api/UserApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { Eye, EyeOff } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const { setLoading } = useLoader();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOtpForm, setShowOtpForm] = useState<boolean>(false);
  const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState<any>({});
  const [passwordErrors, setPasswordErrors] = useState<any>({});

  const validatePassword = () => {
    let errors: any = {};

    if (!password) {
      errors.password = "Password is required";
    } else {
      if (password.length < 8) {
        errors.passwordLen = "Password must be at least 8 characters";
      }
      if (!/[A-Z]/.test(password)) {
        errors.passwordUCase = "Must contain at least one uppercase letter";
      }
      if (!/[a-z]/.test(password)) {
        errors.passwordLCase = "Must contain at least one lowercase letter";
      }
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return errors;
  };

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const validateField = () => {
    let errors: any = {};

    if (!email || email.trim() === "") {
      errors.email = "Email is required";
    } else if (!isValidEmail(email)) {
      errors.email = "Invalid email format";
    }

    return errors;
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [setLoading]);

  const handleOtpRequest = async (e: any) => {
    e.preventDefault();
    const validationErrors = validateField();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;
    setLoading(true);
    try {
      const res = await requestOtp(email);
      dynamicSuccess(res.message);
      setShowOtpForm(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (err: any) {
      dynamicFailed(err?.response?.data?.message || err?.message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value;
    if (/^\d$/.test(value)) {
      e.target.value = value;
      if (index < inputsRef.current.length - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    } else {
      e.target.value = "";
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (
      e.key === "Backspace" &&
      !inputsRef.current[index]?.value &&
      index > 0
    ) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otp = inputsRef.current.map((input) => input.value).join("");
    setLoading(true);
    try {
      const res = await otpVerify(email, Number(otp));
      dynamicSuccess(res.message);
      setShowOtpForm(false);
      setShowPasswordForm(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (err: any) {
      dynamicFailed(err?.response?.data?.message || err?.message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validatePassword();
    setPasswordErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;
    setLoading(true);
    try {
      await resetPassword(email, password, confirmPassword);
      dynamicSuccess("Password updated successfully!");
      setShowOtpForm(false);
      setShowPasswordForm(false);
      navigate("/login");
    } catch (err: any) {
      dynamicFailed(err?.response?.data?.message || err?.message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return !showOtpForm && !showPasswordForm ? (
    <div className="login-container d-flex justify-content-center align-items-center p-3">
      <div className="login-box text-center">
        <div className="logo mb-3">
          <img src={logo} alt="logo" className="logo-img" />
        </div>

        <h1 className="welcome-text mb-4">Welcome!</h1>

        <form onSubmit={handleOtpRequest}>
          <div className="mb-3 text-start">
            <input
              type="text"
              className="form-control custom-input"
              placeholder="Your Email / Phone number"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && (
              <small className="error-message">{errors.email}</small>
            )}
          </div>

          <button type="submit" className="btn btn-danger w-100 custom-btn">
            Send OTP code in email
          </button>
        </form>

        <p className="mt-3 signup-text">
          Don’t have an account{" "}
          <span className="signup-link">Sign up here!</span>
        </p>
      </div>
    </div>
  ) : showOtpForm ? (
    <div className="login-container d-flex justify-content-center align-items-center p-3">
      <div className="login-box text-center">
        <div className="logo mb-3">
          <img src={logo} alt="logo" className="logo-img" />
        </div>

        <h1 className="welcome-text mb-4">Enter OTP</h1>

        <form onSubmit={handleOtpSubmit}>
          <div className="d-flex justify-content-center gap-2 mb-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                ref={(el) => {
                  if (el) inputsRef.current[index] = el;
                }}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="form-control text-center custom-otp-input"
                style={{
                  width: "40px",
                  height: "50px",
                  fontSize: "24px",
                  borderRadius: "10px",
                }}
              />
            ))}
          </div>

          <button type="submit" className="btn btn-danger w-100 custom-btn">
            Verify OTP
          </button>
        </form>

        <p className="mt-3 signup-text">
          Did not receive it?{" "}
          <span className="signup-link" onClick={handleOtpRequest}>
            Resend OTP
          </span>
        </p>
      </div>
    </div>
  ) : (
    <div className="login-container d-flex justify-content-center align-items-center p-3">
      <div className="login-box text-center">
        <div className="logo mb-3">
          <img src={logo} alt="logo" className="logo-img" />
        </div>

        <h1 className="welcome-text mb-4">Welcome!</h1>

        <form onSubmit={handlePasswordSubmit}>
          <div className="mb-3">
            <input
              type="text"
              className="form-control custom-input"
              placeholder="Your Email / Phone number"
              value={email}
              disabled
            />
          </div>

          <div className="mb-3 password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control custom-input"
              placeholder="Create secure password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              <i className="password-toggle-icon">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</i>
            </button>
          </div>

          <div className="mb-4 password-input-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="form-control custom-input"
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              <i className="password-toggle-icon">{showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}</i>
            </button>
          </div>

          <button type="submit" className="btn btn-danger w-100 custom-btn">
            Submit
          </button>
        </form>

        <div className="text-start password-details">
          <p className="mt-3 mb-1 signup-text">Password must be</p>
          <ul className="signup-text">
            <li className={`${passwordErrors.passwordLen && "password-error-message"}`}>
              At least 8 characters long
            </li>
            <li
              className={`${(passwordErrors.passwordUCase || passwordErrors.passwordLCase) && "password-error-message"}`}
            >
              A mix of upper/lowercase letters, numbers
            </li>
            <li>Used for only one account</li>
            {passwordErrors.password && (
              <li className="password-error-message">{passwordErrors.password}</li>
            )}
            {passwordErrors.confirmPassword && (
              <li className="password-error-message">
                {passwordErrors.confirmPassword}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
